/**
 * Data Service - Simulates database queries for the PropertyPro dashboard
 * Loads data from the local JSON database and provides methods
 * that mirror Supabase query patterns.
 */

import databaseJson from '../data/database.json';

const database = databaseJson;

// Simulate async delay like a real database
const simulateDelay = (ms = 100) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get all property transactions (raw data)
 */
export async function getPropertyTransactions(projectId = null) {
  await simulateDelay();

  let transactions = database.property_transactions;

  if (projectId) {
    transactions = transactions.filter(tx => tx.project_id === projectId);
  }

  return { data: transactions, error: null };
}

/**
 * Get aggregated property transactions view (for the data table)
 */
export async function getAggregatedTransactions(projectId = null) {
  await simulateDelay();

  let transactions = database.vw_project_property_transactions;

  if (projectId) {
    transactions = transactions.filter(tx => tx.project_id === projectId);
  }

  return { data: transactions, error: null };
}

/**
 * Get chart data for visualizations
 */
export async function getChartData() {
  await simulateDelay();
  return { data: database.chart_data, error: null };
}

/**
 * Get all projects
 */
export async function getProjects(organisationId = null) {
  await simulateDelay();

  let projects = database.projects;

  if (organisationId) {
    projects = projects.filter(p => p.organisation_id === organisationId);
  }

  return { data: projects, error: null };
}

/**
 * Get all organisations
 */
export async function getOrganisations() {
  await simulateDelay();
  return { data: database.organisations, error: null };
}

/**
 * Get database metadata
 */
export async function getMetadata() {
  await simulateDelay();
  return { data: database.metadata, error: null };
}

/**
 * Supabase-compatible query builder for property_transactions
 * Usage: query('property_transactions').select('*').eq('project_id', id)
 */
export function query(table) {
  let data = database[table] || [];
  let filters = [];

  const builder = {
    select: (columns = '*') => {
      return builder;
    },
    eq: (column, value) => {
      filters.push(row => row[column] === value);
      return builder;
    },
    gt: (column, value) => {
      filters.push(row => parseFloat(row[column]) > value);
      return builder;
    },
    lt: (column, value) => {
      filters.push(row => parseFloat(row[column]) < value);
      return builder;
    },
    gte: (column, value) => {
      filters.push(row => parseFloat(row[column]) >= value);
      return builder;
    },
    lte: (column, value) => {
      filters.push(row => parseFloat(row[column]) <= value);
      return builder;
    },
    like: (column, pattern) => {
      const regex = new RegExp(pattern.replace(/%/g, '.*'), 'i');
      filters.push(row => regex.test(row[column]));
      return builder;
    },
    order: (column, { ascending = true } = {}) => {
      data = [...data].sort((a, b) => {
        if (ascending) {
          return a[column] > b[column] ? 1 : -1;
        }
        return a[column] < b[column] ? 1 : -1;
      });
      return builder;
    },
    limit: (count) => {
      data = data.slice(0, count);
      return builder;
    },
    then: async (resolve) => {
      await simulateDelay();
      let result = data;
      for (const filter of filters) {
        result = result.filter(filter);
      }
      resolve({ data: result, error: null });
    }
  };

  return builder;
}

/**
 * Create a Supabase-like client interface
 */
export const localSupabase = {
  from: (table) => query(table),
  rpc: async (funcName, params) => {
    await simulateDelay();
    if (funcName === 'get_transaction_summary') {
      return { data: database.metadata, error: null };
    }
    return { data: null, error: { message: 'Function not found' } };
  }
};

export default {
  getPropertyTransactions,
  getAggregatedTransactions,
  getChartData,
  getProjects,
  getOrganisations,
  getMetadata,
  query,
  localSupabase
};
